
import { AppSpinner } from "./app-spinner.js";
import { IconCheck } from "./icons/check.js";
import { IconChevronDown } from "./icons/chevron-down.js";
import { IconTerminal } from "./icons/terminal.js";

const styles = css`
  :host { display: block; min-width: 0; }
  .activity { color: var(--muted-foreground); }
  button { align-items: center; background: transparent; border: 0; border-radius: var(--radius-sm); color: inherit; cursor: pointer; display: flex; font: inherit; gap: 6px; max-width: 100%; padding: 2px; text-align: left; width: 100%; }
  button:hover { background: color-mix(in oklab, var(--accent) 20%, transparent); }
  button:focus-visible { box-shadow: 0 0 0 2px var(--ring-shadow); outline: none; }
  .tool-icon { align-items: center; color: color-mix(in oklab, var(--muted-foreground) 65%, transparent); display: inline-flex; flex: 0 0 auto; height: 20px; justify-content: center; width: 20px; }
  .tool-icon > * { height: 14px; opacity: 0.8; width: 14px; }
  .summary { align-items: baseline; display: flex; flex: 1; gap: 6px; min-width: 0; overflow: hidden; }
  .title { color: color-mix(in oklab, var(--foreground) 82%, transparent); flex: 0 0 auto; font-size: 0.75rem; font-weight: 500; line-height: 20px; }
  .detail { color: color-mix(in oklab, var(--muted-foreground) 55%, transparent); flex: 1; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.75rem; line-height: 20px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .controls { align-items: center; color: color-mix(in oklab, var(--muted-foreground) 55%, transparent); display: flex; flex: 0 0 auto; gap: 1px; }
  .control { align-items: center; display: inline-flex; height: 16px; justify-content: center; width: 16px; }
  .control > * { height: 12px; width: 12px; }
  .control.chevron > * { transition: transform 160ms ease; }
  .control.chevron.expanded > * { transform: rotate(180deg); }
  .error { color: var(--destructive); }
  pre { border-left: 1px solid color-mix(in oklab, var(--border) 45%, transparent); color: var(--muted-foreground); cursor: text; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.6875rem; line-height: 1.65; margin: 4px 0 2px 28px; max-height: 256px; overflow: auto; padding: 2px 0 2px 12px; white-space: pre-wrap; }
  @media (prefers-reduced-motion: reduce) { .control.chevron > * { transition: none; } }
`;

export class AppToolActivity extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppToolActivity)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["activityid","data-activity-id","seq","data-seq","title","data-title","detail","data-detail","content","data-content","status","data-status","iserror","data-is-error","subagentkey","data-subagent-key"];

  #activityId = "";
  #seq = 0;
  #title = "Tool";
  #detail = "";
  #content = "";
  #status = "finished";
  #isError = false;
  #subagentKey = "";
  #visible;
  #activityIdSet = false;
  #seqSet = false;
  #titleSet = false;
  #detailSet = false;
  #contentSet = false;
  #statusSet = false;
  #isErrorSet = false;
  #subagentKeySet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #attr0;
  #attr1;
  #attr2;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #attrLast4;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get activityId() {
    return this.#activityId;
  }

  set activityId(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#activityId, next)) return;
    this.#activityId = next;
    this.#activityIdSet = true;
    this.#updateBindings('activityId');
  }

  get seq() {
    return this.#seq;
  }

  set seq(value) {
    const raw = value ?? 0;
    const next = raw == null ? raw : Number(raw);
    if (Object.is(this.#seq, next)) return;
    this.#seq = next;
    this.#seqSet = true;
    this.#updateBindings('seq');
  }

  get title() {
    return this.#title;
  }

  set title(value) {
    const raw = value ?? "Tool";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#title, next)) return;
    this.#title = next;
    this.#titleSet = true;
    this.#updateBindings('title');
  }

  get detail() {
    return this.#detail;
  }

  set detail(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#detail, next)) return;
    this.#detail = next;
    this.#detailSet = true;
    this.#updateBindings('detail');
  }

  get content() {
    return this.#content;
  }

  set content(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#content, next)) return;
    this.#content = next;
    this.#contentSet = true;
    this.#updateBindings('content');
  }

  get status() {
    return this.#status;
  }

  set status(value) {
    const raw = value ?? "finished";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#status, next)) return;
    this.#status = next;
    this.#statusSet = true;
    this.#updateBindings('status');
  }

  get isError() {
    return this.#isError;
  }

  set isError(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#isError, next)) return;
    this.#isError = next;
    this.#isErrorSet = true;
    this.#updateBindings('isError');
  }

  get subagentKey() {
    return this.#subagentKey;
  }

  set subagentKey(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#subagentKey, next)) return;
    this.#subagentKey = next;
    this.#subagentKeySet = true;
    this.#updateBindings('subagentKey');
  }

  #setVisible(value) {
    if (Object.is(this.#visible, value)) return;
    this.#visible = value;
    this.#updateBindings('visible');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-activity-id')) this.activityId = this.dataset.activityId;
    if (this.hasAttribute('data-seq')) this.seq = this.dataset.seq;
    if (this.hasAttribute('data-title')) this.title = this.dataset.title;
    if (this.hasAttribute('data-detail')) this.detail = this.dataset.detail;
    if (this.hasAttribute('data-content')) this.content = this.dataset.content;
    if (this.hasAttribute('data-status')) this.status = this.dataset.status;
    if (this.hasAttribute('data-is-error')) this.isError = this.dataset.isError;
    if (this.hasAttribute('data-subagent-key')) this.subagentKey = this.dataset.subagentKey;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.activityId = props.activityId;
      this.seq = props.seq;
      this.title = props.title;
      this.detail = props.detail;
      this.content = props.content;
      this.status = props.status;
      this.isError = props.isError;
      this.subagentKey = props.subagentKey;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#activityIdSet) this.activityId = this.dataset.activityId;
    if (!this.#seqSet) this.seq = this.dataset.seq;
    if (!this.#titleSet) this.title = this.dataset.title;
    if (!this.#detailSet) this.detail = this.dataset.detail;
    if (!this.#contentSet) this.content = this.dataset.content;
    if (!this.#statusSet) this.status = this.dataset.status;
    if (!this.#isErrorSet) this.isError = this.dataset.isError;
    if (!this.#subagentKeySet) this.subagentKey = this.dataset.subagentKey;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'activityid':
      case 'data-activity-id':
        this.activityId = newValue;
        break;
      case 'seq':
      case 'data-seq':
        this.seq = newValue;
        break;
      case 'title':
      case 'data-title':
        this.title = newValue;
        break;
      case 'detail':
      case 'data-detail':
        this.detail = newValue;
        break;
      case 'content':
      case 'data-content':
        this.content = newValue;
        break;
      case 'status':
      case 'data-status':
        this.status = newValue;
        break;
      case 'iserror':
      case 'data-is-error':
        this.isError = newValue;
        break;
      case 'subagentkey':
      case 'data-subagent-key':
        this.subagentKey = newValue;
        break;
    }
  }

  #initState() {
    this.#visible = false;
  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="${this.#escAttr(`activity${this.#isError ? " error" : ""}`)}" data-argon-bind="0"><button type="button" data-activity-id="${this.#escAttr(this.#activityId)}" data-seq="${this.#seq}" aria-expanded="${this.#escAttr(this.#visible ? "true" : "false")}" data-argon="3018" data-argon-bind="1"><span class="tool-icon" aria-hidden="true"><icon-terminal><!--argon-text:0-->${""}<!--/argon-text:0--></icon-terminal></span><span class="summary"><span class="title"><!--argon-text:1-->${this.#escText((this.#status === "running") ? `Running ${this.#title}` : this.#title)}<!--/argon-text:1--></span><!--argon-text:2-->${(this.#detail !== "") ? `<span class="detail">${this.#escText(this.#detail)}</span>` : ""}<!--/argon-text:2--></span><span class="controls" aria-hidden="true"><span class="${this.#escAttr(`control chevron${this.#visible ? " expanded" : ""}`)}" data-argon-bind="2"><icon-chevron-down><!--argon-text:3-->${""}<!--/argon-text:3--></icon-chevron-down></span><span class="control"><!--argon-text:4-->${(this.#status === "running") ? `<app-spinner>${""}</app-spinner>` : `<icon-check>${""}</icon-check>`}<!--/argon-text:4--></span></span></button><!--argon-text:5-->${((this.#subagentKey === "") && this.#visible) ? `<pre>${this.#escText((this.#content !== "") ? this.#content : ((this.#status === "running") ? "Waiting for output…" : "No output"))}</pre>` : ""}<!--/argon-text:5--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#attr2 = root.querySelector('[data-argon-bind="2"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'status' || prop === 'title') {
      const __argonNext = `${this.#escText((this.#status === "running") ? `Running ${this.#title}` : this.#title)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'detail') {
      const __argonNext = `${(this.#detail !== "") ? `<span class="detail">${this.#escText(this.#detail)}</span>` : ""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined || prop === 'status') {
      const __argonNext = `${(this.#status === "running") ? `<app-spinner>${""}</app-spinner>` : `<icon-check>${""}</icon-check>`}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if (prop === undefined || prop === 'subagentKey' || prop === 'visible' || prop === 'content' || prop === 'status') {
      const __argonNext = `${((this.#subagentKey === "") && this.#visible) ? `<pre>${this.#escText((this.#content !== "") ? this.#content : ((this.#status === "running") ? "Waiting for output…" : "No output"))}</pre>` : ""}`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'isError') && this.#attr0) {
      const __argonNext = `${`activity${this.#isError ? " error" : ""}`}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('class', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'activityId') && this.#attr1) {
      const __argonNext = `${this.#activityId}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr1.setAttribute('data-activity-id', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'seq') && this.#attr1) {
      const __argonNext = `${this.#seq}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr1.setAttribute('data-seq', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'visible') && this.#attr1) {
      const __argonNext = `${this.#visible ? "true" : "false"}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr1.setAttribute('aria-expanded', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'visible') && this.#attr2) {
      const __argonNext = `${`control chevron${this.#visible ? " expanded" : ""}`}`;
      if (__argonNext !== this.#attrLast4) {
        this.#attrLast4 = __argonNext;
        this.#attr2.setAttribute('class', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '3018').forEach(el => this.#listen(el, '3018', 'click', () => { if (this.#subagentKey)
    this.dispatchEvent(new CustomEvent("subagent-open", { bubbles: true, composed: true, detail: { agentKey: this.#subagentKey } }));
else
    this.#setVisible(!this.#visible); }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
